import { useEffect, useState, VoidFunctionComponent } from 'react';
import { ThreeEvent } from '@react-three/fiber';
import { useConnect } from 'wagmi';
import { Line, Plane, Text } from '@react-three/drei';
import { ConnectorButton } from '@/components/ConnectorButton';
import { Screen } from '@/components/Screen';
import { Helix } from '@/components/Helix';
import { useResponsive } from '@/hooks/useResponsive';
import { Image } from '@/components/Image';
import { garamondNarrowLight, helveticaNeue, monaco } from '@/lib/fonts';
import recaptchaImg1 from '@/img/recaptcha-image-1.png';
import recaptchaImg2 from '@/img/recaptcha-image-2.png';

const DISCLAIMER_IMG_2_ASPECT = 234 / 34;
const DISCLAIMER_IMG_2_FACTOR = 0.2;

const googleDisclaimerProps = {
    textAlign: 'left' as const,
    font: monaco,
    fontSize: 0.1,
    color: 'white',
    maxWidth: 3,
    lineHeight: 1.2,
    anchorY: 'middle' as const,
};

interface Props {
    goToNextScene: () => void;
    connectionErrorMessage: string | null;
}

export const Connect: VoidFunctionComponent<Props> = ({ goToNextScene, connectionErrorMessage }) => {
    const { connectors } = useConnect();
    const { responsiveValue } = useResponsive();
    const [hovered, setHovered] = useState<boolean>(false);

    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };
    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    return (
        <>
            <Screen>
                <Helix />
                <Text
                    position={[0, responsiveValue(2.05, 2.2), 0]}
                    textAlign="center"
                    font={garamondNarrowLight}
                    fontSize={responsiveValue(0.35, 0.28)}
                    color="white"
                    maxWidth={responsiveValue(5, 4)}
                    lineHeight={responsiveValue(1.2, 1.1)}
                >
                    CRYPTOGRAPHIC SIGNATURE REQUIRED
                </Text>
                {connectionErrorMessage !== null && (
                    <Plane
                        scale={responsiveValue(1, 1.3)}
                        position={[0, 1.25, 0.1]}
                        material-color="#F00"
                        args={[2.75, 0.65]}
                    >
                        <Text
                            textAlign="center"
                            font={helveticaNeue}
                            fontSize={0.135}
                            color="white"
                            anchorX="center"
                            maxWidth={2.75}
                        >
                            {connectionErrorMessage}
                        </Text>
                    </Plane>
                )}
                <group
                    scale={responsiveValue(0.9, 1)}
                    position={responsiveValue([4.15, -1.1, 0], [1.9, -1.6, 0])}
                    rotation={[0, 0, Math.PI / 2]}
                >
                    <Image position={[-1.7, 0, 0]} url={recaptchaImg1} scale={responsiveValue(0.25, 0.2)} />
                    <Text {...googleDisclaimerProps}>This site is protected by reCAPTCHA and the Google</Text>
                    <Text
                        {...googleDisclaimerProps}
                        onClick={() => window.open('https://policies.google.com/privacy', '_blank')}
                        onPointerOver={over}
                        onPointerOut={out}
                        position={[-0.65, -0.06, 0]}
                    >
                        Privacy Policy
                    </Text>
                    <Text {...googleDisclaimerProps} position={[-0.1, -0.06, 0]}>
                        and
                    </Text>
                    <Text
                        {...googleDisclaimerProps}
                        onPointerOver={over}
                        onPointerOut={out}
                        onClick={() => window.open('https://policies.google.com/terms', '_blank')}
                        position={[0.53, -0.06, 0]}
                    >
                        Terms of Service
                    </Text>
                    <Text {...googleDisclaimerProps} position={[1.25, -0.06, 0]}>
                        apply.
                    </Text>
                    <Image
                        position={[2.2, 0, responsiveValue(0, -100)]}
                        url={recaptchaImg2}
                        scale={[DISCLAIMER_IMG_2_FACTOR * DISCLAIMER_IMG_2_ASPECT, DISCLAIMER_IMG_2_FACTOR, 1]}
                    />
                </group>
                <group scale={responsiveValue(0.95, 1)} position={[0, responsiveValue(-0.5, -1.05), 0]}>
                    <Text
                        position={[0, 0, 0]}
                        textAlign="center"
                        font={garamondNarrowLight}
                        fontSize={responsiveValue(0.27, 0.23)}
                        color="white"
                        maxWidth={responsiveValue(8, 4)}
                        lineHeight={1}
                        anchorY="bottom"
                    >
                        Connect Using:
                    </Text>
                    <Line
                        position={[0, -0.02, 0]}
                        points={[
                            [responsiveValue(-2, -1.7), 0, 0],
                            [responsiveValue(2, 1.7), 0, 0],
                        ]}
                        color="white"
                        lineWidth={0.4}
                    />
                    <group
                        scale={responsiveValue(1, 0.85)}
                        position={responsiveValue([-1.2, -0.8, 0], [-1.48, -0.73, 0])}
                    >
                        {connectors.map((connector, i) => (
                            <group key={i} position={[0, -(i * responsiveValue(0.8, 0.95)), 0]}>
                                <ConnectorButton
                                    goToNextScene={goToNextScene}
                                    key={connector.id}
                                    connector={connector}
                                    fontSize={responsiveValue(0.28, 0.3)}
                                />
                            </group>
                        ))}
                    </group>
                </group>
            </Screen>
        </>
    );
};
